import { useQueries, useQuery } from '@tanstack/react-query';
import { firestore } from '../services/firebase';
import { USER, USERS } from '../constants/entities';
import { auth } from '../services/firebase';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

// Normaliza uma data de compra (Guru manda segundos/ms/ISO; nós gravamos
// Firestore Timestamp em lastUpdatedAt) para milissegundos — formato que o
// hasSchedule e o calculatesSchedule consomem (String(created).length === 13).
const normalizeToMillis = (value) => {
  if (value == null) return null;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
  if (typeof value === 'string') {
    if (/^\d+$/.test(value)) {
      const n = Number(value);
      return n < 1e12 ? n * 1000 : n;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const useUser = () => {
  return useQuery({
    queryKey: [USER, auth.currentUser?.uid],
    queryFn: async () => {
      if (!auth.currentUser?.uid) {
        throw new Error('No user ID found.');
      }

      const userDocRef = doc(firestore, USERS, auth.currentUser?.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        return {
          Celular: '',
          created_at: null,
          Email: '',
          Nome_Completo: '',
          isSignUpFromApp: null,
        };
      }

      return {id: auth.currentUser?.uid, ...userDoc.data()};
    },
    enabled: !!auth.currentUser?.uid,
  });
};

// Autores de comentários, respostas e mensagens — só o que a lista precisa
// mostrar (nome, foto e se é admin).
//
// Antes cada CommentItem e cada ReplyItem fazia o seu próprio `getDoc` em
// `users/{autorId}` no mount, sem cache e sem deduplicação: dez comentários do
// mesmo aluno eram dez leituras, refeitas do zero a cada remontagem da lista.
// Era esse ida-e-volta que fazia as linhas sumirem e voltarem.
//
// Uma entrada de cache POR uid, e não uma chave única com o conjunto de ids:
// a chave por conjunto mudaria a cada comentário novo e invalidaria tudo
// justamente quando a lista cresce. Por uid a chave é estável, o dedupe entre
// comentários e respostas sai de graça e o cache é compartilhado entre telas.
export const usePublicUsers = (userIds = []) => {
  const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));

  const results = useQueries({
    queries: uniqueIds.map((userId) => ({
      queryKey: [USER, 'public', userId],
      queryFn: async () => {
        const userDoc = await getDoc(doc(firestore, USERS, userId));
        if (!userDoc.exists()) return null;

        const data = userDoc.data();
        return {
          name: data?.Nome_Completo || null,
          photo: data?.photo || null,
          isAdmin: data?.isAdmin === true,
        };
      },
      staleTime: 1000 * 60 * 10,
      gcTime: 1000 * 60 * 30,
      // O documento de outro aluno pode estar fora do alcance das regras, e
      // insistir não muda isso. Quem chama renderiza com o autor gravado no
      // próprio comentário (ou "Aluno") — a linha nunca some por causa disto.
      retry: false,
    })),
  });

  const authorsById = new Map();
  uniqueIds.forEach((userId, index) => {
    const author = results[index]?.data;
    if (author) authorsById.set(userId, author);
  });

  return authorsById;
};

export const useUserPlan = () => {
  const userId = auth.currentUser?.uid;

  return useQuery({
    queryKey: [USER, userId, 'plan'],
    queryFn: async () => {
      if (!userId) {
        throw new Error('No user ID found.');
      }

      const userDocRef = doc(firestore, USERS, userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('User document not found.');
      }

      const userData = userDoc.data();
      const plan = userData?.plan;
      let userPlan = false;

      if (plan) {
        const subscriptionsRef = collection(userDocRef, 'subscriptions');
        const subscriptionsSnapshot = await getDocs(subscriptionsRef);

        subscriptionsSnapshot.forEach((subscriptionDoc) => {
          const subscriptionData = subscriptionDoc.data();

          if (subscriptionData.items) {
            const activeItem = subscriptionData.items.find(
              (item) => item.active && item.product === plan
            );

            if (activeItem) {
              userPlan = {
                plan: plan,
                created: activeItem.created,
              };
            }
          }
        });

        if (userPlan) {
          return userPlan;
        }
      }

      if (userData.productIds) {
        const subscriptionsRef = collection(userDocRef, 'subscriptions');
        const subscriptionsSnapshot = await getDocs(subscriptionsRef);

        for (const product of userData.productIds) {
          if (product.customid) {
            subscriptionsSnapshot.forEach((subscriptionDoc) => {
              const subscriptionData = subscriptionDoc.data();

              if (subscriptionDoc.id === product.customid) {
                userPlan = {
                  plan: subscriptionData.productId,
                  created: subscriptionData.date_ms,
                };
              }
            });
          } else {
            const productId = product.productId;
            subscriptionsSnapshot.forEach((subscriptionDoc) => {
              const subscriptionData = subscriptionDoc.data();

              const matchingItem = subscriptionData.items?.find(
                (item) => item.plan?.product === productId
              );

              if (matchingItem) {
                userPlan = {
                  plan: productId,
                  created: subscriptionData.dates?.started_at,
                };
              }
            });
          }
        }

        // Compra AVULSA (tipo "produto"): a data da compra vive na subcoleção
        // `payments` (webhook de transação da Guru), não em `subscriptions`.
        // Sem isto o agendamento "N dias após a compra" não funcionava para
        // produtos — só para assinatura. Busca a data da compra do produto e a
        // normaliza para ms (formato aceito pelo hasSchedule/calculatesSchedule).
        if (!userPlan) {
          const paymentsRef = collection(userDocRef, 'payments');
          const paymentsSnapshot = await getDocs(paymentsRef);

          for (const product of userData.productIds) {
            const productId = product.productId;
            paymentsSnapshot.forEach((paymentDoc) => {
              if (userPlan) return;
              const paymentData = paymentDoc.data();
              const matchesProduct =
                paymentData.items?.[0]?.price?.product?.id === productId ||
                String(paymentData.product?.marketplace_id) === String(productId) ||
                paymentData.productId === productId;

              if (matchesProduct) {
                const rawDate =
                  paymentData.dates?.created_at ??
                  paymentData.dates?.confirmed_date ??
                  paymentData.created_at ??
                  paymentData.date_ms ??
                  paymentData.lastUpdatedAt;
                const created = normalizeToMillis(rawDate);
                if (created) {
                  userPlan = { plan: productId, created };
                }
              }
            });
            if (userPlan) break;
          }
        }

        if (userPlan) {
          return userPlan;
        }
      }

      return userPlan;
    },
    enabled: !!userId,
  });
};

